import { pushService } from "@/services/pushService";

const SERVICE_WORKER_URL = "/push-worker.js";

export function isPushSupported(): boolean {
  return (
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function isPushPermissionDenied(): boolean {
  return "Notification" in window && Notification.permission === "denied";
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  return Uint8Array.from(rawData, (char) => char.charCodeAt(0));
}

export async function ensurePushSubscription(): Promise<boolean> {
  if (!isPushSupported() || isPushPermissionDenied()) {
    return false;
  }

  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    return false;
  }

  const registration =
    await navigator.serviceWorker.register(SERVICE_WORKER_URL);
  await navigator.serviceWorker.ready;

  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    const { public_key } = await pushService.getVapidPublicKey();
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(public_key) as BufferSource,
    });
  }

  await pushService.saveSubscription(subscription.toJSON());
  return true;
}

export async function removePushSubscription(): Promise<void> {
  if (!isPushSupported()) return;
  const registration =
    await navigator.serviceWorker.getRegistration(SERVICE_WORKER_URL);
  const subscription = await registration?.pushManager.getSubscription();
  if (!subscription) return;
  await pushService.deleteSubscription(subscription.endpoint);
  await subscription.unsubscribe();
}
